import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api from "../api";
import { House, CalendarCheck, Wrench, Users, CurrencyCircleDollar, DotsThree, Receipt, Gear, Briefcase, SignOut, Question, UserPlus, ChatCircle, FileText } from "@phosphor-icons/react";
import { LegalLinks } from "./LegalLinks";

const NAVS = {
  operator: [
    { to: "/", label: "Today", icon: CalendarCheck },
    { to: "/instructions", label: "Add-ons", icon: Wrench },
    { to: "/quotes", label: "Quotes", icon: FileText },
    { to: "/invoices", label: "Invoices", icon: Receipt },
    { to: "/messages", label: "Messages", icon: ChatCircle },
    { to: "/more", label: "More", icon: DotsThree },
  ],
  owner: [
    { to: "/owner", label: "Home", icon: House },
    { to: "/owner/requests", label: "Requests", icon: Wrench },
    { to: "/owner/jobs", label: "Jobs", icon: Briefcase },
    { to: "/owner/invoices", label: "Invoices", icon: CurrencyCircleDollar },
    { to: "/owner/settings", label: "Settings", icon: Gear },
  ],
  subcontractor: [
    { to: "/sub", label: "Jobs", icon: Briefcase },
    { to: "/leads", label: "Leads", icon: UserPlus },
  ],
};
NAVS.dispatcher = NAVS.operator;

const BADGE_KEYS = { "Today": "today", "Add-ons": "addons", "Invoices": "invoices", "Requests": "requests", "Jobs": "jobs", "More": "more", "Messages": "messages" };
// prefixes that make a bottom-nav item "active" even on sub-pages (exact match still wins when it's "/")
const ACTIVE_PREFIX = { "/quotes": ["/quotes", "/jobs"], "/more": ["/more", "/properties", "/owners", "/history", "/issues", "/leads", "/metrics", "/settings", "/platform", "/roster", "/payables"] };

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAVS[user?.role] || [];
  const [branding, setBranding] = useState(null);
  const [attn, setAttn] = useState({});

  useEffect(() => {
    if (user?.role === "owner") api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    const load = () => api.get("/attention").then((r) => setAttn(r.data || {})).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap + polling interval by design
  }, []);

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-card">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            {user?.role === "owner" && (branding?.business_logo || branding?.business_name) ? (
              <>
                {branding.business_logo && <img src={branding.business_logo} alt={branding.business_name || "Business"} className="h-7 w-7 rounded-md object-contain" />}
                <span className="font-heading text-lg font-bold tracking-tight" data-testid="app-title">{branding.business_name || "Your operator"} <span className="text-xs font-semibold text-muted-foreground">via RPM Field</span></span>
              </>
            ) : (
              <>
                <img src="/veloktify-icon.svg" alt="Veloktify" className="h-7 w-7 rounded-md" />
                <span className="font-heading text-lg font-bold tracking-tight" data-testid="app-title">RPM Field <span className="text-xs font-semibold text-muted-foreground">by Veloktify</span></span>
              </>
            )}
          </div>
          <Link to="/help" data-testid="help-link"
            className="flex h-10 items-center gap-1 rounded-md px-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">
            <Question size={18} weight="bold" /> Help
          </Link>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}
            className="flex h-10 items-center gap-1 rounded-md px-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">
            <SignOut size={18} weight="bold" /> Out
          </button>
        </div>
        {title && <div className="px-4 pb-3"><h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="page-title">{title}</h1></div>}
      </header>
      {user?.role === "operator" && user.subscription_status !== "active" && (
        <div data-testid="billing-banner" className="border-b border-[hsl(43,74%,50%)] bg-[hsl(43,74%,50%)]/15 px-4 py-2 text-center text-xs font-bold">
          {user.subscription_status === "trialing" && new Date(user.trial_end) > new Date()
            ? `Free trial ends ${new Date(user.trial_end).toLocaleDateString()} — `
            : "Trial ended — "}
          <Link to="/settings/account" className="underline">subscribe to RPM Field Pro</Link>
        </div>
      )}
      <main className="flex-1 px-4 pb-24 pt-4">{children}{user?.role === "owner" && <LegalLinks className="mt-10" />}</main>
      <nav className="fixed bottom-0 left-1/2 z-20 w-full max-w-lg -translate-x-1/2 border-t border-border bg-card" data-testid="bottom-nav">
        <div className="flex">
          {items.map((it) => {
            const active = loc.pathname === it.to || (ACTIVE_PREFIX[it.to] || []).some((p) => loc.pathname.startsWith(p));
            const Icon = it.icon;
            return (
              <Link key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase()}`}
                className={`relative flex h-16 flex-1 flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon size={24} weight={active ? "bold" : "regular"} />
                {it.label}
                {(attn[BADGE_KEYS[it.label]] || 0) > 0 && (
                  <span data-testid={`badge-${it.label.toLowerCase()}`} className="absolute right-3 top-1.5 font-heading text-xl font-bold leading-none text-destructive">*</span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
